import * as Bixby from '$/core';
import dayjs from 'dayjs';
import { MsalService } from './MsalService';
import { PlannerTranscriptionService } from './PlannerTranscriptionService';

export abstract class TranscriptionsAdminService {
  private static baseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_TRANSCRIPTIONSCONTROLLER}`;

  public static async getById(transcriptionId: string): Promise<Bixby.Models.AdminCompanyTranscription> {
    const url = `${TranscriptionsAdminService.baseUrl}/${transcriptionId}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  public static async getTranscriptions(): Promise<Bixby.Models.AdminCompanyTranscription[]> {
    const url = `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SEARCH}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: Bixby.Models.TranscriptionAdminRequest = {
      transcriptionFileStates: [
        Bixby.Enums.TranscriptionFileState.NotSubmitted,
        Bixby.Enums.TranscriptionFileState.Submitted,
        Bixby.Enums.TranscriptionFileState.Processing,
        Bixby.Enums.TranscriptionFileState.Staged,
        Bixby.Enums.TranscriptionFileState.Error,
      ],
    };
    return await Bixby.API.post(route, request);
  }

  public static async getTranscriptionsByCompanyId(
    companyId: string
  ): Promise<Bixby.Models.AdminCompanyTranscription[]> {
    const url = `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SEARCH}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: Bixby.Models.TranscriptionAdminRequest = {
      companyId,
    };
    return await Bixby.API.post(route, request);
  }

  public static async savePlannerApprovalTask(transcriptionId: string, plannerTranscriptionApproveId: string) {
    const url = `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_TRANSCRIPT_SAVE_PLANNER_APPROVAL_TASK}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: Bixby.Models.UpdatePlannerApprovalTaskRequest = {
      transcriptionId,
      plannerTranscriptionApproveId,
    };
    return await Bixby.API.post(route, request);
  }

  public static async deleteTranscription(transcriptionId: string, permanentDelete = false): Promise<Bixby.Models.DeleteTranscriptionResult> {
    const url = `${TranscriptionsAdminService.baseUrl}/${transcriptionId}?permanentDelete=${permanentDelete}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete<Bixby.Models.DeleteTranscriptionResult>(route);
  }

  public static async deleteTranscriptionAndCreateReuploadTaskAsync(
    transcriptionId: string
  ): Promise<{ plannerTaskCreated: boolean | null }> {
    const result = await TranscriptionsAdminService.deleteTranscription(transcriptionId, false);

    if (!result.newsExists || !result.newTranscriptionId) {
      return { plannerTaskCreated: null };
    }

    const transcriptionDetails: Bixby.Models.NewsTranscriptionDetails = {
      assignees: [MsalService.getAccount().localAccountId],
      bixbyId: result.companyBixbyId ?? '',
      client_Id: result.clientId ?? '',
      client_Name: result.clientName ?? '',
      client_DataSiteEmail: result.clientDataSiteEmail ?? '',
      company_Id: result.companyId ?? '',
      companyTranscription_Id: result.newTranscriptionId,
      plannerTranscriptionCreateId: '',
      replayGoodFrom: result.replayGoodFrom ? dayjs(result.replayGoodFrom).format('MM/DD/YYYY') : '',
      replayGoodUntil: result.replayGoodUntil ? dayjs(result.replayGoodUntil).format('MM/DD/YYYY') : '',
      replayPhoneNumber: result.replayPhoneNumber ?? '',
      replayPin: result.replayPin ?? '',
      replayStreamingUrl: result.replayStreamingUrl ?? '',
      replayType: result.replayType ?? Bixby.Enums.ReplayType.Phone,
      hasPrivateDeclaration: result.hasPrivateDeclaration ?? false,
    };

    try {
      const plannerTaskId = await PlannerTranscriptionService.addTranscriptionCreateTaskAsync(transcriptionDetails);
      try {
        await TranscriptionsAdminService.savePlannerCreateTask(result.newTranscriptionId, plannerTaskId);
      } catch (e) {
        await PlannerTranscriptionService.deleteTranscriptionTaskAsync(plannerTaskId);
        throw e;
      }
      return { plannerTaskCreated: true };
    } catch {
      return { plannerTaskCreated: false };
    }
  }

  public static async savePlannerCreateTask(transcriptionId: string, plannerTranscriptionCreateId: string) {
    const url = `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_TRANSCRIPT_SAVE_PLANNER_CREATE_TASK}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: Bixby.Models.UpdatePlannerCreateTaskRequest = {
      transcriptionId,
      plannerTranscriptionCreateId,
    };
    return await Bixby.API.post(route, request);
  }

  public static async uploadTranscript(transcriptionId: string, audioFile: Blob, transcriptDate: Date) {
    const route = Bixby.API.ServerRoute.forAction(
      `${TranscriptionsAdminService.baseUrl}/${transcriptionId}/${Bixby.Constants.API_ACTION_TRANSCRIPT_UPLOAD}`
    );

    const formData = new FormData();
    formData.set(
      'request',
      JSON.stringify({
        transcriptionId,
        transcriptDate: transcriptDate.toISOString(),
      })
    );

    formData.append('transcriptionAudioFile', audioFile);

    return await Bixby.API.postJSON(route, formData);
  }

  public static async updateTranscriptDate(transcriptionId: string, transcriptDate: Date) {
    const route = Bixby.API.ServerRoute.forAction(
      `${TranscriptionsAdminService.baseUrl}/${transcriptionId}/update/date`
    );
    return await Bixby.API.post(route, { transcriptionId, transcriptDate: transcriptDate.toISOString() });
  }

  public static async downloadTranscriptionAudio(
    transcriptionId: string,
    container: Bixby.Enums.TranscriptionContainer
  ): Promise<Blob> {
    const baseUrl = `${TranscriptionsAdminService.baseUrl}/${transcriptionId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_AUDIO}`;
    const url = `${baseUrl}/${container}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.getFile(route);
  }

  public static async downloadTranscriptionText(
    transcriptionId: string,
    container: Bixby.Enums.TranscriptionContainer
  ): Promise<Blob> {
    const baseUrl = `${TranscriptionsAdminService.baseUrl}/${transcriptionId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_TEXT}`;
    const url = `${baseUrl}/${container}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.getFile(route);
  }

  public static async publishLiveTranscription(value: Bixby.Models.PublishTranscriptionRequest, file?: File) {
    const url = `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_PUBLISH}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.set('request', JSON.stringify(value));
    if (file) {
      formData.append('file', file);
    }

    return await Bixby.API.postJSON(route, formData);
  }

  public static async publishDraftTranscription(value: Bixby.Models.PublishTranscriptionRequest, file?: File) {
    const url = `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_PUBLISH}/${Bixby.Constants.API_PARAMS_DRAFT}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.set('request', JSON.stringify(value));
    if (file) {
      formData.append('file', file);
    }

    return await Bixby.API.postJSON(route, formData);
  }

  public static async republishTranscriptionToCreditsights(transcriptionId: string): Promise<void> {
    //allow any server exceptions through so that client error handling logic is processed
    const route = Bixby.API.ServerRoute.forAction(
      `${TranscriptionsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_REPUBLISH}/${transcriptionId}`
    );
    return await Bixby.API.post(route, transcriptionId);
  }

  public static async updateTranscriptText(transcriptionId: string, file: File) {
    const url = `${TranscriptionsAdminService.baseUrl}/${transcriptionId}/${Bixby.Constants.API_ACTION_UPDATE}/${Bixby.Constants.API_ACTION_TEXT}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const formData = new FormData();
    formData.set('file', file);

    return await Bixby.API.postJSON(route, formData);
  }
}
